import { computed, observable } from 'mobx';
import { autobind } from 'core-decorators';
import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import useModal from '@src/utils/hooks/use-modal';
import LoginStore from '@src/utils/store/login-store';
import Toast from '@src/components/toast';
import { bindBackpackRole, claimBackpackPrize, loadBackpackRewards } from '@src/services/backpack';
import { loadTeamContext } from '@src/services/team';
import RoleBindingModal from './components/role-binding-modal';
import RoleBindingModalStore from './components/role-binding-modal/store';
import ClaimSuccessModal from './components/claim-success-modal';
import ClaimSuccessModalStore from './components/claim-success-modal/store';
import BusinessAccountInfoStore from '@src/business-components/business-account-info';
import rootStore from '@src/mobx-store/root-store';
import $$tracker from '@src/tracker';
import { getClaimRewardFailureReason } from '@src/tracker/business-context';

const BINDING_SUCCESS_MESSAGE = '角色绑定成功，请重新点击领取奖励';
const LOAD_FAILED_MESSAGE = '加载失败，请稍后重试';
const BIND_FAILED_MESSAGE = '角色绑定失败，请稍后重试';
const CLAIM_FAILED_MESSAGE = '领取失败，请稍后重试';
const NAVIGATION_ROUTE_KEYS = ['TEAM', 'BACKPACK', 'RULES'];

/* 置灰点击原因文案（2026-09-17 用户决策：按钮置灰仍可点击，按序 Toast 不可领取原因，不触发领取请求）：
   加载/处理中 → 数据异常 → 已领取（终态优先）→ 领取时间窗口（prize_time 0/2）→ 无分享资格（引导文案） */
const CLAIM_PENDING_MESSAGE = '奖励加载中，请稍后';
const REWARD_UNAVAILABLE_MESSAGE = '奖励数据加载失败，请稍后重试';
const CLAIM_COLLECTED_MESSAGE = '奖励已领取';
const CLAIM_TIME_NOT_STARTED_MESSAGE = '请在公测开服后领奖，请耐心等待';
const CLAIM_TIME_ENDED_MESSAGE = '领取时间已结束';
const CLAIM_NO_QUALIFICATION_MESSAGE = '分享海报后可领取奖励，请先前往生成海报';

// 奖品名与数值保留在 PSD 固定卡片里；本常量只维护它们的稳定档位标识与人数阈值
const TEAM_REWARD_STAGE_DEFINITIONS = Object.freeze([
  Object.freeze({ key: 'two', requiredMemberCount: 2 }),
  Object.freeze({ key: 'three', requiredMemberCount: 3 }),
  Object.freeze({ key: 'four', requiredMemberCount: 4 })
]);

function getErrorMessage(error, fallback) {
  return error && typeof error.message === 'string' && error.message ? error.message : fallback;
}

class BackpackStore extends BaseStore {
  loginStore;

  loadRewards;

  loadTeamContext;

  bindRole;

  claimPrize;

  accountInfoStore = new BusinessAccountInfoStore();

  @observable loadStatus = 'idle';

  // 奖励适配层产出的稳定 `share_poster` ViewModel；null 表示后端未建立任何受支持的奖励
  @observable.ref reward = null;

  // 权威响应中的绑定信息，或仅由本页实例成功提交过的绑定
  @observable.ref binding = null;

  // 响应归一化由 Team 适配层负责；本页只消费其稳定的成员数
  @observable memberCount = 0;

  // 奖励领取时间窗口枚举（服务层 prize_time：0 未开始 / 1 进行中 / 2 已结束；claimable 的组成条件之一）。
  // 不驱动渲染故为普通字段：仅供置灰点击时区分「时间未开始/已结束」与「无分享资格」两种 Toast 原因。
  prizeTime = null;

  @observable bindingPending = false;

  @observable claimPending = false;

  initializePromise = null;

  initializeOwner = undefined;

  initializeVersion = 0;

  pageExposureReported = false;

  // 当前已提交的奖励、绑定与 Team 事实所属的登录账号
  pageContextOwner = undefined;

  bindingPromise = null;

  claimPromise = null;

  roleBindingModalStore;

  claimSuccessModalStore;

  roleBindingModal;

  claimSuccessModal;

  /**
   * 构建带可替换适配边界的页面 owner，供聚焦的 Store 测试注入。
   * @param {object} options 页面依赖。
   * @param {LoginStore} options.loginStore 共享的 XPASS 登录边界。
   * @param {object} options.activitySessionStore 共享活动会话 owner。
   * @param {object} options.teamFactStore 用户级队伍事实快照（小程序分享 reaction 数据源）。
   * @param {Function} options.loadRewards 稳定的奖励上下文 provider。
   * @param {Function} options.loadTeamContext 稳定的 Team ViewModel provider。
   * @param {Function} options.bindRole 角色绑定适配器。
   * @param {Function} options.claimPrize 奖励领取适配器。
   */
  constructor({
    loginStore = new LoginStore(),
    activitySessionStore = rootStore.activitySessionStore,
    teamFactStore = rootStore.teamFactStore,
    loadRewards = loadBackpackRewards,
    loadTeamContext: loadTeam = loadTeamContext,
    bindRole = bindBackpackRole,
    claimPrize = claimBackpackPrize
  } = {}) {
    super();
    this.loginStore = loginStore;
    this.activitySessionStore = activitySessionStore;
    this.teamFactStore = teamFactStore;
    this.loadRewards = loadRewards;
    this.loadTeamContext = loadTeam;
    this.bindRole = bindRole;
    this.claimPrize = claimPrize;
    this.roleBindingModalStore = new RoleBindingModalStore({ submitBinding: this.bindSelectedRole });
    this.claimSuccessModalStore = new ClaimSuccessModalStore();
  }

  /** 注册页面持有的弹窗，路由挂载后仅初始化一次页面。 */
  use() {
    super.use();
    this.roleBindingModal = useModal(RoleBindingModal, {
      store: this.roleBindingModalStore,
      onRequestClose: this.roleBindingModalStore.requestClose
    });
    this.claimSuccessModal = useModal(ClaimSuccessModal, {
      store: this.claimSuccessModalStore,
      onRequestClose: this.claimSuccessModalStore.requestClose
    });
    useMount(this.initialize);
  }

  get currentOwner() {
    const accountInfo = this.loginStore && this.loginStore.businessAccountInfo;
    return accountInfo && accountInfo.username ? accountInfo.username : null;
  }

  @computed
  get operationLocked() {
    return this.loadStatus === 'loading' || this.bindingPending || this.claimPending;
  }

  @computed
  get claiming() {
    return this.claimPending;
  }

  /**
   * 清空账号持有的页面事实，保留调用方指定的生命周期状态。
   * @param {string} loadStatus owner 上下文清空后仍对外可见的生命周期状态。
   * @returns {void}
   */
  clearPageContext(loadStatus = 'idle') {
    this.reward = null;
    this.binding = null;
    this.memberCount = 0;
    this.prizeTime = null;
    this.pageContextOwner = undefined;
    this.loadStatus = loadStatus;
  }

  /** 使变更请求标识失效，防止旧账号的结算解锁新账号的操作。 */
  invalidateMutations() {
    this.bindingPromise = null;
    this.bindingPending = false;
    this.claimPromise = null;
    this.claimPending = false;
  }

  /**
   * 使页面持有的弹窗展示与全部账号级弹窗草稿失效。
   * @returns {void}
   */
  invalidateOwnerModals() {
    this.roleBindingModalStore.resetForOwnerChange();
    this.claimSuccessModalStore.resetForOwnerChange();
  }

  /** 登出或账号替换后，把页面恢复到显式可重新初始化的状态。 */
  resetForOwnerChange() {
    this.initializeVersion += 1;
    this.initializePromise = null;
    this.initializeOwner = undefined;
    this.invalidateMutations();
    this.invalidateOwnerModals();
    this.clearPageContext('idle');
  }

  /** 校验账号级页面事实是否仍属于当前登录的 XPASS 账号。 */
  hasCurrentPageContext() {
    const owner = this.currentOwner;
    if (this.loginStore.isLogin && owner && this.pageContextOwner === owner) {
      return true;
    }

    this.resetForOwnerChange();
    return false;
  }

  /**
   * 确认某次初始化仍持有活跃请求槽位且属于当前登录账号。
   * @param {Promise} request 正在落定的初始化请求。
   * @param {string} owner 请求发起时捕获的 XPASS 账号。
   * @param {number} version 请求发起时捕获的初始化代际。
   * @returns {boolean} 结果是否允许改写当前页面上下文。
   */
  isCurrentInitialization(request, owner, version) {
    return this.initializePromise === request && this.initializeVersion === version && this.currentOwner === owner && this.loginStore.isLogin;
  }

  /**
   * 仅当被废弃的初始化仍占用活跃槽位时清理遗留 owner 状态。
   * @param {Promise} request 正在落定的初始化请求。
   * @param {string} owner 请求发起时捕获的 XPASS 账号。
   * @param {number} version 请求发起时捕获的初始化代际。
   * @returns {void}
   */
  recoverStaleInitialization(request, owner, version) {
    if (this.initializePromise === request && this.initializeVersion === version && (this.currentOwner !== owner || !this.loginStore.isLogin)) {
      this.resetForOwnerChange();
    }
  }

  /**
   * 确认绑定/领取结果同时属于其请求槽位与当前页面 owner。
   * @param {string} type 变更槽位名：`binding` 或 `claim`。
   * @param {Promise} request 正在落定的变更请求。
   * @param {string} owner 变更发起时捕获的页面 owner。
   * @returns {boolean} 结果是否允许改写账号持有的页面状态。
   */
  isCurrentMutation(type, request, owner) {
    return this[`${type}Promise`] === request && this.currentOwner === owner && this.pageContextOwner === owner && this.loginStore.isLogin;
  }

  /**
   * 重置被废弃的 owner 状态，且不让旧变更解锁新请求槽位。
   * @param {string} type 变更槽位名：`binding` 或 `claim`。
   * @param {Promise} request 正在落定的变更请求。
   * @param {string} owner 变更发起时捕获的页面 owner。
   * @returns {void}
   */
  recoverStaleMutation(type, request, owner) {
    if (this[`${type}Promise`] === request && (this.currentOwner !== owner || this.pageContextOwner !== owner || !this.loginStore.isLogin)) {
      this.resetForOwnerChange();
    }
  }

  /**
   * 并行加载奖励与 Team 两路适配数据，只允许最新登录 owner 的结果提交。
   * 未登录进入保持可浏览，不请求账号级数据。
   * @returns {Promise<object|undefined>} 已提交的页面上下文；恢复的失败/过期请求返回 undefined。
   */
  @autobind
  initialize() {
    // await 前置 refresh：门控判定前确保服务端账号就绪——username/isLogin/owner 读的是 get_config
    // 返回的 account（observable），同步分流会在请求在途时误判未登录；此处强制重拉并等待，
    // 右上角、门控与 owner 同源回到服务端视角（过期/跨标签漂移由此纠正）。
    // 失败静默吞掉：account 保持缓存值继续分流，行为不劣于旧缓存态，报错由数据链路负责。
    return Promise.resolve()
      .then(() => this.activitySessionStore.refresh())
      .catch(() => undefined)
      .then(() => this.initializeWithSessionAccount());
  }

  /** 门控与主流程：服务端账号就绪后执行（owner / isLogin 均为最新服务端视角）。 */
  initializeWithSessionAccount() {
    const owner = this.currentOwner;
    if (!this.loginStore.isLogin) {
      // 已登出或刚切换的 owner 不得保留早前账号请求的事实
      this.resetForOwnerChange();
      this.loadStatus = 'ready';
      if (!this.pageExposureReported) {
        $$tracker.pageActivityBag();
        this.pageExposureReported = true;
      }
      return Promise.resolve(undefined);
    }

    if (this.initializePromise && this.initializeOwner === owner) {
      return this.initializePromise;
    }

    this.initializeVersion += 1;
    const version = this.initializeVersion;
    this.invalidateMutations();
    this.invalidateOwnerModals();
    this.clearPageContext('loading');

    let rewardResult;
    let teamResult;
    try {
      rewardResult = this.loadRewards();
    } catch (error) {
      rewardResult = Promise.reject(error);
    }
    try {
      // 两个 provider 先都启动再等待，包括兄弟 provider 同步抛错的情形
      teamResult = this.loadTeamContext();
    } catch (error) {
      teamResult = Promise.reject(error);
    }

    let request;
    request = Promise.all([Promise.resolve(rewardResult), Promise.resolve(teamResult)])
      .then(([rewardContext, teamViewModel]) => {
        if (!this.isCurrentInitialization(request, owner, version)) {
          this.recoverStaleInitialization(request, owner, version);
          return undefined;
        }
        if (!rewardContext || typeof rewardContext !== 'object' || !teamViewModel || !Number.isFinite(teamViewModel.memberCount)) {
          throw new Error(LOAD_FAILED_MESSAGE);
        }

        this.reward = rewardContext.reward || null;
        this.binding = rewardContext.binding || null;
        this.prizeTime = rewardContext.prizeTime === null ? null : rewardContext.prizeTime;
        this.memberCount = teamViewModel.memberCount;
        this.pageContextOwner = owner;
        this.loadStatus = 'ready';
        if (!this.pageExposureReported) {
          $$tracker.pageActivityBag();
          this.pageExposureReported = true;
        }
        // 登记用户级队伍事实（本页并行持有的 team_config）：全局分享 reaction 统一推送
        this.teamFactStore.update({ hasTeam: teamViewModel.hasTeam, teamId: teamViewModel.teamId });
        return { reward: this.reward, binding: this.binding, memberCount: this.memberCount };
      })
      .catch(error => {
        if (!this.isCurrentInitialization(request, owner, version)) {
          this.recoverStaleInitialization(request, owner, version);
          return undefined;
        }

        this.invalidateOwnerModals();
        this.clearPageContext('error');
        Toast.fail(getErrorMessage(error, LOAD_FAILED_MESSAGE));
        return undefined;
      })
      .then(result => {
        if (this.isCurrentInitialization(request, owner, version)) {
          this.initializePromise = null;
          this.initializeOwner = undefined;
        } else {
          this.recoverStaleInitialization(request, owner, version);
        }
        return result;
      });

    this.initializePromise = request;
    this.initializeOwner = owner;
    return request;
  }

  /**
   * 按当前权威状态进入登录、角色绑定或唯一受支持的奖励领取。
   * autobind 入口直接传给 PSD 承接的领取按钮。
   * @returns {*|Promise<*>|undefined} 登录/弹窗结果、共享领取请求；动作不合法时返回 undefined。
   */
  @autobind
  handleClaimClick() {
    if (!this.loginStore.isLogin) {
      if (this.pageContextOwner !== undefined || this.initializePromise || this.bindingPromise || this.claimPromise) {
        this.resetForOwnerChange();
      } else {
        this.invalidateOwnerModals();
      }
      return this.loginStore.signIn();
    }

    if (this.bindingPending || this.loadStatus === 'loading') {
      Toast.fail(CLAIM_PENDING_MESSAGE);
      return undefined;
    }
    if (!this.hasCurrentPageContext()) {
      return undefined;
    }
    if (this.claimPromise) {
      return this.claimPromise;
    }
    // 置灰可点击（2026-09-17 用户决策）：以下各态按钮保持置灰但点击送达，按序 Toast 不可领取原因后终止；
    // 已领取（claimStatus=2）是终态，优先于时间窗口判定，避免「时间已结束」掩盖「已领取」
    if (!this.reward || this.reward.prizeId !== 'share_poster') {
      Toast.fail(REWARD_UNAVAILABLE_MESSAGE);
      return undefined;
    }
    // 枚举非法（服务层归一化为 null）按数据异常反馈，不误导为分享引导
    if (this.reward.claimStatus === null || this.prizeTime === null) {
      Toast.fail(REWARD_UNAVAILABLE_MESSAGE);
      return undefined;
    }
    if (this.reward.claimStatus === 2) {
      Toast.fail(CLAIM_COLLECTED_MESSAGE);
      return undefined;
    }
    if (this.prizeTime === 0) {
      Toast.fail(CLAIM_TIME_NOT_STARTED_MESSAGE);
      return undefined;
    }
    if (this.prizeTime === 2) {
      Toast.fail(CLAIM_TIME_ENDED_MESSAGE);
      return undefined;
    }
    if (this.reward.claimStatus !== 1 || !this.reward.claimable) {
      Toast.fail(CLAIM_NO_QUALIFICATION_MESSAGE);
      return undefined;
    }

    if (!this.binding) {
      if (!this.roleBindingModal) {
        return undefined;
      }
      const draftRequest = this.roleBindingModalStore.openForBinding();
      $$tracker.clickClaimReward('fail', 'role_unbound');
      this.roleBindingModal.open();
      return draftRequest;
    }

    this.claimPending = true;
    const operationOwner = this.pageContextOwner;
    let claimResult;
    try {
      claimResult = this.claimPrize(this.reward.prizeId);
    } catch (error) {
      claimResult = Promise.reject(error);
    }

    let request;
    request = Promise.resolve(claimResult)
      .then(() => {
        if (!this.isCurrentMutation('claim', request, operationOwner)) {
          return false;
        }

        // 先提交终态再挂载成功展示，防止第二次点击与弹窗装配竞态
        this.reward = { ...this.reward, claimStatus: 2, claimable: false };
        if (this.claimSuccessModal) {
          this.claimSuccessModal.open();
        }
        $$tracker.clickClaimReward('success', '');
        return true;
      })
      .catch(error => {
        if (this.isCurrentMutation('claim', request, operationOwner)) {
          Toast.fail(getErrorMessage(error, CLAIM_FAILED_MESSAGE));
          $$tracker.clickClaimReward('fail', getClaimRewardFailureReason(error));
        }
        return false;
      })
      .then(result => {
        if (this.isCurrentMutation('claim', request, operationOwner)) {
          this.claimPending = false;
          this.claimPromise = null;
        } else {
          this.recoverStaleMutation('claim', request, operationOwner);
        }
        return result;
      });

    this.claimPromise = request;
    return request;
  }

  /**
   * 把弹窗选定的完整角色提交到本页实例，不自动领取奖励。
   * 严格 `true` 才算履约子弹窗 Store 契约；失败只 Toast 一次并以 `false` 结算供重试。
   * @param {object} binding 弹窗选择，含角色/区服名与 ID。
   * @returns {Promise<boolean>|undefined} 共享的绑定结果；当前不允许绑定时返回 undefined。
   */
  @autobind
  bindSelectedRole(binding) {
    if (!this.hasCurrentPageContext()) {
      return undefined;
    }
    if (this.bindingPromise) {
      return this.bindingPromise;
    }
    if (!this.loginStore.isLogin || this.loadStatus === 'loading' || this.claimPending || !this.reward || !this.reward.claimable || this.binding) {
      return undefined;
    }

    this.bindingPending = true;
    const operationOwner = this.pageContextOwner;
    let bindResult;
    try {
      bindResult = this.bindRole(binding);
    } catch (error) {
      bindResult = Promise.reject(error);
    }

    let request;
    request = Promise.resolve(bindResult)
      .then(() => {
        if (!this.isCurrentMutation('binding', request, operationOwner)) {
          return false;
        }
        this.binding = { ...binding };
        Toast.success(BINDING_SUCCESS_MESSAGE);
        return true;
      })
      .catch(error => {
        if (this.isCurrentMutation('binding', request, operationOwner)) {
          Toast.fail(getErrorMessage(error, BIND_FAILED_MESSAGE));
        }
        return false;
      })
      .then(result => {
        if (this.isCurrentMutation('binding', request, operationOwner)) {
          this.bindingPending = false;
          this.bindingPromise = null;
        } else {
          this.recoverStaleMutation('binding', request, operationOwner);
        }
        return result;
      });

    this.bindingPromise = request;
    return request;
  }

  /**
   * 页面操作解锁时仅导航已知底栏路由 key。
   * 当前 BACKPACK key 刻意空操作，避免产生重复 history 记录。
   * @param {string} routeKey MoBottomTabs 发出的受控 key。
   * @returns {undefined}
   */
  @autobind
  navigateByTab(routeKey) {
    if (this.operationLocked || routeKey === 'BACKPACK' || NAVIGATION_ROUTE_KEYS.indexOf(routeKey) === -1) {
      return undefined;
    }

    if (routeKey === 'TEAM' && !this.activitySessionStore.reviewCleared) {
      this.redirect('MAIN');
      return undefined;
    }
    this.redirect(routeKey);
    return undefined;
  }

  @computed
  /** 仅把 Team 适配层的 memberCount 映射到三个 PSD 固定奖励卡。 */
  get teamRewardStages() {
    return TEAM_REWARD_STAGE_DEFINITIONS.map(stage => ({
      ...stage,
      achieved: this.memberCount >= stage.requiredMemberCount
    }));
  }

  @computed
  /** 适配层未证明可领取，或任一页面操作进行中时，领取命中区保持 inert。 */
  get claimDisabled() {
    return this.operationLocked || !this.reward || this.reward.prizeId !== 'share_poster' || this.reward.claimStatus !== 1 || !this.reward.claimable;
  }
}

export default BackpackStore;
